import { useEffect, useMemo, useState } from 'react';
import { api } from '../lib/api.js';
import { ghs, toPesewas } from '../lib/money.js';
import { Button, Chip, Banner, Field } from '../components/ui.jsx';
import { Sheet, Divider, Section } from './SalesPoint.jsx';

const card = {
  background: 'var(--color-bg-surface)', border: '1px solid var(--color-border-default)',
  borderRadius: 'var(--radius-lg)', overflow: 'hidden'
};

const ERRORS = {
  reason_required: 'A reason is required. Six months from now it is the only thing that explains this.',
  service_already_here: 'That service is already in this category.',
  staff_already_here: 'Somebody with that name is already on the team.',
  category_required: 'Pick a category or type a new one.',
  name_required: 'A name is required.',
  already_retired: 'It is already retired.',
  category_is_retired: 'Bring the category back first, then the service.',
  forbidden: 'Your role does not include this.'
};
const readable = e => ERRORS[e?.data?.error] || 'That did not work.';

// ---------------------------------------------------------------- services

/**
 * The price list, grouped by category.
 *
 * A service always belongs to exactly one category: the category is the parent and is the
 * thing somebody scans for at the counter. Adding a service either picks a category that
 * exists or names a new one, and never leaves the service loose.
 *
 * Nothing is deleted. Sale lines months old point at these rows, so retiring is a state
 * with a date and a reason.
 */
export function Catalogue({ slug, canEdit }) {
  const b = useMemo(() => api.b(slug), [slug]);
  const [data, setData] = useState({ services: [], categories: [] });
  const [showRetired, setShowRetired] = useState(false);
  const [adding, setAdding] = useState(false);
  const [retiring, setRetiring] = useState(null);
  const [note, setNote] = useState(null);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState({});

  const load = () => b.catalogueServices(showRetired).then(setData).catch(() => {});
  useEffect(() => { load(); }, [slug, showRetired]);

  async function act(fn, message) {
    setError(null); setNote(null);
    try { const r = await fn(); await load(); setNote(typeof message === 'function' ? message(r) : message); }
    catch (e) { setError(readable(e)); }
  }

  /**
   * One field on one service. Saved on its own rather than through a form, because the job
   * this screen actually gets used for is putting a price up across a dozen rows, and a
   * save button per row would make that twelve extra clicks.
   */
  async function save(service, patch) {
    setError(null);
    try {
      const { changed } = await b.updateService(service.id, patch);
      if (changed) {
        await load();
        const what = Object.keys(patch)[0];
        setNote(`${service.name}: ${what === 'price' ? 'price' : what === 'durationMin' ? 'time' : 'name'} updated. Past sales keep what they were rung up at.`);
      }
    } catch (e) { setError(readable(e)); await load(); }
  }

  const grouped = data.categories.map(c => ({
    category: c,
    services: data.services.filter(s => s.category_id === c.id)
  })).filter(g => g.services.length || showRetired);

  const loose = data.services.filter(s => !data.categories.some(c => c.id === s.category_id));

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
        <div style={{ flex: 1 }} className="t-body-m secondary">
          Everything the salon sells, under the category it belongs to. Click a name, a time
          or a price to change it. Nothing is ever deleted, and no past sale moves: every one
          keeps the price it was rung up at.
        </div>
        <Button size="m" variant="ghost" style={{ border: '1px solid var(--color-border-default)' }}
          onClick={() => setShowRetired(v => !v)}>
          {showRetired ? 'Hide retired' : 'Show retired'}
        </Button>
        {canEdit && <Button onClick={() => setAdding(true)}>Add a service</Button>}
      </div>

      {error && <Banner tone="danger">{error}</Banner>}
      {note && <Banner tone="success">{note}</Banner>}

      {grouped.map(({ category, services }) => {
        const shown = open[category.id] !== false;
        return (
          <div key={category.id} style={{ ...card, opacity: category.active ? 1 : 0.6 }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '12px 16px', background: 'var(--color-bg-subtle)' }}>
              <span className="cat-dot" style={{ '--cat': category.colour }} />
              <button onClick={() => setOpen(o => ({ ...o, [category.id]: !shown }))}
                className="t-heading-s" style={{ border: 0, background: 'transparent', cursor: 'pointer', font: 'inherit', color: 'inherit', padding: 0 }}>
                {category.name}
              </button>
              <span className="t-body-s muted" style={{ flex: 1 }}>
                {services.filter(s => s.active).length} service{services.filter(s => s.active).length === 1 ? '' : 's'}
                {!category.active && ', retired'}
              </span>
              {canEdit && category.active && (
                <Button size="m" variant="ghost" style={{ border: '1px solid var(--color-border-default)' }}
                  onClick={() => setRetiring({ kind: 'category', row: category })}>Retire the category</Button>
              )}
              {canEdit && !category.active && (
                <Button size="m" variant="secondary"
                  onClick={() => act(() => b.restoreCategory(category.id), `${category.name} is back. Its services stay retired until you bring each one back.`)}>
                  Bring back
                </Button>
              )}
            </div>

            {shown && services.map(s => (
              <div key={s.id} data-testid="service-row" data-name={s.name}
                style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '7px 16px', borderTop: '1px solid var(--color-border-default)' }}>

                <Editable value={s.name} disabled={!canEdit || !s.active}
                  style={{ flex: 1, opacity: s.active ? 1 : 0.55 }} className="t-body-m"
                  onSave={v => save(s, { name: v })} />

                {!s.active && <Chip tone="danger">Retired</Chip>}

                <Editable value={s.duration_min ?? ''} disabled={!canEdit || !s.active}
                  placeholder="no time" suffix=" min" align="right" numeric width={90}
                  className="t-body-s muted"
                  onSave={v => save(s, { durationMin: v === '' ? null : Number(v) })} />

                <Editable
                  value={s.price == null ? '' : (s.price / 100).toFixed(2)}
                  disabled={!canEdit || !s.active}
                  placeholder="on request" prefix={s.price_from ? 'from GHS ' : 'GHS '} align="right"
                  numeric width={150} className="money t-money-m"
                  onSave={v => save(s, { price: v === '' ? null : toPesewas(v) })} />

                {canEdit && (s.active
                  ? <Button size="m" variant="ghost" style={{ border: '1px solid var(--color-border-default)' }}
                      onClick={() => setRetiring({ kind: 'service', row: s })}>Retire</Button>
                  : <Button size="m" variant="secondary"
                      onClick={() => act(() => b.restoreService(s.id), `${s.name} is back on the picker.`)}>Bring back</Button>)}
              </div>
            ))}
          </div>
        );
      })}

      {loose.length > 0 && (
        <Banner tone="warning" title="Services with no category">
          {loose.length} service{loose.length === 1 ? '' : 's'} point at a category that is
          gone. They cannot be found on the picker until they are moved.
        </Banner>
      )}

      {adding && (
        <AddService b={b} categories={data.categories.filter(c => c.active)}
          onClose={() => setAdding(false)}
          onAdded={r => {
            setAdding(false); load();
            setNote(r.categoryCreated
              ? `${r.service.name} added, and the category ${r.category.name} was created for it.`
              : `${r.service.name} added under ${r.category.name}.`);
          }} />
      )}

      {retiring && (
        <RetireSheet
          title={retiring.kind === 'category' ? `Retire ${retiring.row.name}` : `Retire ${retiring.row.name}`}
          lede={retiring.kind === 'category'
            ? 'The category and every service under it come off the picker. Past sales are untouched.'
            : 'It comes off the picker and out of the booking list. Every past sale that included it is untouched.'}
          onClose={() => setRetiring(null)}
          onConfirm={reason => {
            const { kind, row } = retiring;
            setRetiring(null);
            act(
              () => kind === 'category' ? b.retireCategory(row.id, reason) : b.retireService(row.id, reason),
              r => kind === 'category'
                ? `${row.name} retired, with ${r.servicesRetired} service${r.servicesRetired === 1 ? '' : 's'} under it.`
                : `${row.name} retired.` + (r.upcomingBookings
                    ? ` ${r.upcomingBookings} upcoming booking${r.upcomingBookings === 1 ? '' : 's'} still name it, so somebody has to ring those clients.`
                    : '')
            );
          }} />
      )}
    </div>
  );
}

function AddService({ b, categories, onClose, onAdded }) {
  const [form, setForm] = useState({
    name: '', categoryId: categories[0]?.id || '', categoryName: '',
    price: '', durationMin: '', priceFrom: false
  });
  const [newCategory, setNewCategory] = useState(!categories.length);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = p => setForm(f => ({ ...f, ...p }));

  const ready = form.name.trim() && (newCategory ? form.categoryName.trim() : form.categoryId) && !busy;

  async function submit(e) {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      onAdded(await b.addService({
        name: form.name,
        categoryId: newCategory ? undefined : Number(form.categoryId),
        categoryName: newCategory ? form.categoryName : undefined,
        price: form.price === '' ? null : toPesewas(form.price),
        durationMin: form.durationMin === '' ? null : Number(form.durationMin),
        priceFrom: form.priceFrom
      }));
    } catch (err) { setError(readable(err)); setBusy(false); }
  }

  return (
    <Sheet title="Add a service" subtitle="Every service sits under one category." onClose={onClose}>
      <form onSubmit={submit} style={{ display: 'contents' }}>
        <Section label="CATEGORY, THE PARENT">
          {!newCategory ? (
            <>
              <select value={form.categoryId} onChange={e => set({ categoryId: e.target.value })}
                style={{
                  height: 48, padding: '0 12px', borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--color-border-strong)', background: 'var(--color-bg-surface)',
                  color: 'var(--color-text-primary)', font: '400 15px/22px var(--font-ui)'
                }}>
                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <button type="button" className="btn btn-ghost btn-m" style={{ justifySelf: 'start', padding: '4px 0' }}
                onClick={() => setNewCategory(true)}>Or start a new category</button>
            </>
          ) : (
            <>
              <Field id="categoryName" label="NEW CATEGORY" value={form.categoryName} autoFocus
                hint="Typing one that already exists reuses it rather than making a near-duplicate."
                onChange={e => set({ categoryName: e.target.value })} />
              {categories.length > 0 && (
                <button type="button" className="btn btn-ghost btn-m" style={{ justifySelf: 'start', padding: '4px 0' }}
                  onClick={() => setNewCategory(false)}>Or pick an existing one</button>
              )}
            </>
          )}
        </Section>

        <Divider />
        <Section label="THE SERVICE">
          <Field id="svcname" label="NAME" value={form.name} onChange={e => set({ name: e.target.value })} />
          <Field id="price" label="PRICE IN CEDIS" value={form.price} inputMode="decimal"
            hint="Leave it empty for a service quoted on the day."
            onChange={e => set({ price: e.target.value })} />
          <label style={{ display: 'flex', gap: 10, alignItems: 'center', cursor: 'pointer' }}>
            <input type="checkbox" checked={form.priceFrom} onChange={e => set({ priceFrom: e.target.checked })} />
            <span className="t-body-m">This is a starting price, shown as "from"</span>
          </label>
          <Field id="duration" label="HOW LONG IT TAKES, MINUTES" value={form.durationMin} inputMode="numeric"
            hint="Used by the diary to size the slot. Optional." onChange={e => set({ durationMin: e.target.value })} />
        </Section>

        <div style={{ flex: 1 }} />
        <Divider />
        <div style={{ padding: 16, display: 'grid', gap: 10 }}>
          {error && <Banner tone="danger">{error}</Banner>}
          <Button type="submit" disabled={!ready}>{busy ? 'Adding' : 'Add it'}</Button>
        </div>
      </form>
    </Sheet>
  );
}

// ---------------------------------------------------------------- staff records

/**
 * Who works here. Distinct from Users and roles: this is the person who performs the work
 * and earns commission, which is not always somebody who signs in at all.
 */
export function StaffRecords({ slug, canEdit }) {
  const b = useMemo(() => api.b(slug), [slug]);
  const [rows, setRows] = useState([]);
  const [showLeft, setShowLeft] = useState(false);
  const [adding, setAdding] = useState(false);
  const [leaving, setLeaving] = useState(null);
  const [rates, setRates] = useState(null);
  const [note, setNote] = useState(null);
  const [error, setError] = useState(null);

  const load = () => b.staffRecords(showLeft).then(d => setRows(d.staff)).catch(() => {});
  useEffect(() => { load(); }, [slug, showLeft]);

  async function act(fn, message) {
    setError(null); setNote(null);
    try { const r = await fn(); await load(); setNote(typeof message === 'function' ? message(r) : message); }
    catch (e) { setError(readable(e)); }
  }

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
        <div style={{ flex: 1 }} className="t-body-m secondary">
          The people who do the work and earn commission. Somebody can be on this list
          without ever signing in: an account is a separate thing, in Users and roles.
        </div>
        <Button size="m" variant="ghost" style={{ border: '1px solid var(--color-border-default)' }}
          onClick={() => setShowLeft(v => !v)}>{showLeft ? 'Hide people who left' : 'Show people who left'}</Button>
        {canEdit && <Button onClick={() => setAdding(true)}>Add someone</Button>}
      </div>

      {error && <Banner tone="danger">{error}</Banner>}
      {note && <Banner tone="success">{note}</Banner>}

      <div style={card}>
        <div style={{ padding: '13px 16px', background: 'var(--color-bg-subtle)' }} className="t-label-xs muted">
          NAME · ROLE · COMMISSION · SPECIALITIES
        </div>
        {rows.map(s => (
          <div key={s.id} style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '12px 16px', borderTop: '1px solid var(--color-border-default)' }}>
            <div style={{ width: 200, flex: 'none', opacity: s.active ? 1 : 0.55 }}>
              <div className="t-body-m">{s.name}</div>
              <div className="t-body-s muted">{s.job_title || 'staff'}</div>
            </div>

            {!s.active && <Chip tone="danger">Left {s.left_at ? new Date(s.left_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' }) : ''}</Chip>}
            {!s.active && s.left_reason && <span className="t-body-s muted" style={{ flex: 1, minWidth: 0 }}>{s.left_reason}</span>}

            {s.active && (
              <>
                <span className="t-body-s muted" style={{ flex: 1 }}>
                  {s.account_username ? `signs in as @${s.account_username}` : 'no sign in account'}
                </span>
                <span className="money t-money-s" style={{ width: 110, textAlign: 'right' }}>
                  {s.commission_type === 'percent' ? `${s.commission_value}%`
                    : s.commission_type === 'fixed' ? ghs(s.commission_value * 100) : 'none'}
                </span>
                <Chip tone={s.speciality_count ? 'neutral' : 'warning'} showDot={!s.speciality_count}>
                  {s.speciality_count ? `${s.speciality_count} speciality${s.speciality_count === 1 ? '' : ' areas'}` : 'does everything'}
                </Chip>
              </>
            )}

            {canEdit && s.active && (
              <Button size="m" variant="ghost" style={{ border: '1px solid var(--color-border-default)' }}
                onClick={() => setRates(s)}>Rates</Button>
            )}
            {canEdit && (s.active
              ? <Button size="m" variant="ghost" style={{ border: '1px solid var(--color-border-default)' }}
                  onClick={() => setLeaving(s)}>Record that they left</Button>
              : <Button size="m" variant="secondary"
                  onClick={() => act(() => b.returnStaff(s.id), `${s.name} is back on the team, with their history intact.`)}>
                  They are back
                </Button>)}
          </div>
        ))}
        {!rows.length && <div style={{ padding: 40, textAlign: 'center' }} className="t-body-m muted">Nobody on the team yet.</div>}
      </div>

      {rates && <RateSheet b={b} person={rates} onClose={() => setRates(null)} />}

      {adding && <AddStaff b={b} onClose={() => setAdding(false)}
        onAdded={r => { setAdding(false); load();
          setNote(r.returned ? `${r.staff.name} is back, on their original record.` : `${r.staff.name} added to the team.`); }} />}

      {leaving && (
        <RetireSheet
          title={`${leaving.name} is leaving`}
          lede="Their record stays, with the date and the reason. The commission sheet for the month they left still has to name them."
          onClose={() => setLeaving(null)}
          onConfirm={reason => {
            const s = leaving; setLeaving(null);
            act(() => b.retireStaff(s.id, reason), r => {
              const bits = [`${s.name} recorded as having left.`];
              if (r.upcomingBookings) bits.push(`${r.upcomingBookings} upcoming booking${r.upcomingBookings === 1 ? '' : 's'} still name them and need reassigning.`);
              if (r.commissionThisMonth) bits.push(`They earned ${ghs(r.commissionThisMonth)} in commission this month, still to pay.`);
              return bits.join(' ');
            });
          }} />
      )}
    </div>
  );
}

function AddStaff({ b, onClose, onAdded }) {
  const [form, setForm] = useState({
    name: '', jobTitle: '', phone: '', commissionType: 'percent', commissionValue: '40', productCommissionValue: '10'
  });
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = p => setForm(f => ({ ...f, ...p }));

  async function submit(e) {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      onAdded(await b.addStaff({
        ...form,
        commissionValue: Number(form.commissionValue || 0),
        productCommissionValue: Number(form.productCommissionValue || 0)
      }));
    } catch (err) { setError(readable(err)); setBusy(false); }
  }

  return (
    <Sheet title="Add someone to the team" subtitle="What they earn, and what they are allowed to do." onClose={onClose}>
      <form onSubmit={submit} style={{ display: 'contents' }}>
        <Section label="WHO">
          <Field id="stname" label="FULL NAME" value={form.name} autoFocus
            hint="This prints on the receipt beside the work they did."
            onChange={e => set({ name: e.target.value })} />
          <Field id="stjob" label="JOB TITLE" value={form.jobTitle}
            hint="Stylist, therapist, nail tech, barber." onChange={e => set({ jobTitle: e.target.value })} />
          <Field id="stphone" label="PHONE, OPTIONAL" value={form.phone} onChange={e => set({ phone: e.target.value })} />
        </Section>

        <Divider />
        <Section label="COMMISSION">
          <div style={{ display: 'flex', gap: 8 }}>
            {[['percent', 'A percentage'], ['fixed', 'A fixed amount'], ['none', 'None']].map(([v, l]) => (
              <label key={v} style={{
                flex: 1, padding: 10, cursor: 'pointer', textAlign: 'center',
                border: '1px solid ' + (form.commissionType === v ? 'var(--color-accent-default)' : 'var(--color-border-default)'),
                background: form.commissionType === v ? 'var(--color-accent-subtle)' : 'transparent',
                borderRadius: 'var(--radius-md)'
              }}>
                <input type="radio" name="ct" checked={form.commissionType === v}
                  onChange={() => set({ commissionType: v })} style={{ display: 'none' }} />
                <span className="t-body-s">{l}</span>
              </label>
            ))}
          </div>
          {form.commissionType !== 'none' && (
            <Field id="cv" label={form.commissionType === 'percent' ? 'PERCENT OF THE LINE' : 'CEDIS PER LINE'}
              value={form.commissionValue} inputMode="decimal"
              hint="A per service rate can override this later, on the commission screen."
              onChange={e => set({ commissionValue: e.target.value })} />
          )}
        </Section>

        <Divider />
        <Section label="AFTER THIS">
          <div className="t-body-s secondary">
            They can perform anything until you give them a speciality. Set those on the
            Staff screen, and from then on only the categories you tick are offered when
            somebody books them.
          </div>
        </Section>

        <div style={{ flex: 1 }} />
        <Divider />
        <div style={{ padding: 16, display: 'grid', gap: 10 }}>
          {error && <Banner tone="danger">{error}</Banner>}
          <Button type="submit" disabled={busy || !form.name.trim()}>{busy ? 'Adding' : 'Add them'}</Button>
        </div>
      </form>
    </Sheet>
  );
}

/**
 * A field that is text until you click it, and an input while you are in it.
 *
 * Built for the job this screen gets used for: putting prices up across a dozen rows at
 * once. A form with a save button per row would make that twelve extra clicks, and a modal
 * per service would make it worse. Enter commits, Escape puts it back, Tab commits and
 * moves on, so the whole list can be done from the keyboard.
 *
 * It commits only when the value actually changed, so tabbing through a list touches
 * nothing and writes no audit entries for edits nobody made.
 */
function Editable({ value, onSave, disabled, placeholder, prefix = '', suffix = '',
                    align = 'left', numeric, width, className = '', style }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(value ?? ''));
  const [busy, setBusy] = useState(false);
  const original = String(value ?? '');

  useEffect(() => { if (!editing) setDraft(original); }, [original, editing]);

  const commit = async () => {
    setEditing(false);
    if (draft === original) return;
    setBusy(true);
    try { await onSave(draft.trim()); } finally { setBusy(false); }
  };

  const shared = {
    width: width || undefined,
    textAlign: align,
    ...style
  };

  if (disabled) {
    return (
      <span className={className} style={{ ...shared, opacity: 0.55 }}>
        {original ? prefix + original + suffix : placeholder}
      </span>
    );
  }

  if (editing) {
    return (
      <input
        className={className}
        autoFocus
        inputMode={numeric ? 'decimal' : undefined}
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={e => {
          if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); }
          if (e.key === 'Escape') { setDraft(original); setEditing(false); }
        }}
        style={{
          ...shared,
          height: 34, padding: '0 8px',
          border: '1px solid var(--color-accent-default)',
          borderRadius: 'var(--radius-sm)',
          background: 'var(--color-bg-surface)',
          color: 'var(--color-text-primary)',
          outline: 'none'
        }} />
    );
  }

  return (
    <button type="button" onClick={() => setEditing(true)} className={className}
      title="Click to edit"
      style={{
        ...shared,
        height: 34, padding: '0 8px', cursor: 'text',
        border: '1px solid transparent', borderRadius: 'var(--radius-sm)',
        background: busy ? 'var(--color-accent-subtle)' : 'transparent',
        color: original ? 'inherit' : 'var(--color-text-muted)',
        font: 'inherit'
      }}
      onMouseEnter={e => (e.currentTarget.style.borderColor = 'var(--color-border-strong)')}
      onMouseLeave={e => (e.currentTarget.style.borderColor = 'transparent')}>
      {original ? prefix + original + suffix : placeholder}
    </button>
  );
}

// ---------------------------------------------------------------- per service rates

/**
 * One person, one service, one rate. This is the top of the resolution order at the moment
 * of sale: it beats the service's own rule and beats their default, which is how a braider
 * earns 50% on braids and 40% on everything else without inventing a second staff record.
 *
 * Only the exceptions are listed. Everything not on this list falls through to their
 * default, and saying so on the screen is what stops somebody entering all 168.
 */
function RateSheet({ b, person, onClose }) {
  const [data, setData] = useState(null);
  const [services, setServices] = useState([]);
  const [q, setQ] = useState('');
  const [error, setError] = useState(null);

  const load = () => b.staffRates(person.id).then(setData).catch(e => setError(readable(e)));
  useEffect(() => {
    load();
    b.catalogueServices().then(d => setServices(d.services.filter(s => s.active))).catch(() => {});
  }, [person.id]);

  const has = new Set((data?.rates || []).map(r => r.service_id));
  const matches = q.trim()
    ? services.filter(s => !has.has(s.id) && s.name.toLowerCase().includes(q.trim().toLowerCase())).slice(0, 8)
    : [];

  const defaultRule = data
    ? data.staff.commission_type === 'percent' ? `${data.staff.commission_value}%`
      : data.staff.commission_type === 'flat' ? ghs(data.staff.commission_value * 100) : 'no commission'
    : '';

  async function set(serviceId, commissionType, commissionValue) {
    setError(null);
    try { await b.setStaffRate(person.id, serviceId, { commissionType, commissionValue }); await load(); setQ(''); }
    catch (e) { setError(readable(e)); }
  }

  return (
    <Sheet title={`${person.name}'s rates`}
      subtitle={`Exceptions only. Anything not listed pays their default of ${defaultRule}.`}
      onClose={onClose}>

      <Section label="ADD AN EXCEPTION">
        <Field id="rateq" label="FIND A SERVICE" value={q} autoFocus
          hint="Type a few letters. Only services without an exception are offered."
          onChange={e => setQ(e.target.value)} />
        {matches.map(s => (
          <button key={s.id} type="button"
            onClick={() => set(s.id, 'percent', data.staff.commission_value)}
            style={{
              display: 'flex', gap: 10, alignItems: 'center', padding: '10px 12px', cursor: 'pointer',
              border: '1px solid var(--color-border-default)', borderRadius: 'var(--radius-md)',
              background: 'transparent', font: 'inherit', color: 'inherit', textAlign: 'left'
            }}>
            <span className="cat-dot" style={{ '--cat': s.category_colour }} />
            <span className="t-body-m" style={{ flex: 1 }}>{s.name}</span>
            <span className="t-body-s muted">{s.category_name}</span>
          </button>
        ))}
        {q.trim() && !matches.length && (
          <div className="t-body-s muted">Nothing matches, or it already has an exception below.</div>
        )}
      </Section>

      <Divider />
      <Section label={`EXCEPTIONS, ${data?.rates.length || 0}`}>
        {!data?.rates.length && (
          <div className="t-body-s muted">
            None. Every service pays {defaultRule}, which is usually right: a list of
            exceptions is only worth keeping while it stays short.
          </div>
        )}
        {(data?.rates || []).map(r => (
          <div key={r.service_id} style={{
            display: 'flex', gap: 10, alignItems: 'center', padding: '10px 0',
            borderTop: '1px solid var(--color-border-default)'
          }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="t-body-m">{r.service_name}</div>
              <div className="t-body-s muted">{r.category_name}</div>
            </div>
            <select value={r.commission_type}
              onChange={e => set(r.service_id, e.target.value, r.commission_value)}
              style={{
                height: 36, borderRadius: 'var(--radius-sm)', border: '1px solid var(--color-border-strong)',
                background: 'var(--color-bg-surface)', color: 'var(--color-text-primary)', font: '400 14px var(--font-ui)'
              }}>
              <option value="percent">percent</option>
              <option value="flat">flat GHS</option>
              <option value="none">nothing</option>
            </select>
            {r.commission_type !== 'none' && (
              <input className="money" defaultValue={r.commission_value} inputMode="decimal"
                onBlur={e => {
                  const v = Number(e.target.value);
                  if (Number.isFinite(v) && v !== r.commission_value) set(r.service_id, r.commission_type, v);
                }}
                style={{
                  width: 80, height: 36, textAlign: 'right', padding: '0 8px',
                  borderRadius: 'var(--radius-sm)', border: '1px solid var(--color-border-strong)',
                  background: 'var(--color-bg-surface)', color: 'var(--color-text-primary)'
                }} />
            )}
            <Button size="m" variant="ghost" style={{ border: '1px solid var(--color-border-default)' }}
              onClick={async () => { await b.clearStaffRate(person.id, r.service_id); load(); }}>
              Clear
            </Button>
          </div>
        ))}
      </Section>

      <div style={{ flex: 1 }} />
      <Divider />
      <div style={{ padding: 16, display: 'grid', gap: 10 }}>
        {error && <Banner tone="danger">{error}</Banner>}
        <Banner tone="info">
          A rate change applies to sales from now on. Every past sale line carries the rule
          and the amount that were in force when it was rung up, so nothing behind you moves.
        </Banner>
      </div>
    </Sheet>
  );
}

// ---------------------------------------------------------------- the reason gate

/**
 * Nothing is retired without a reason. The field is required by the API too, not just
 * here: six months from now the reason is the only thing that explains the gap.
 */
function RetireSheet({ title, lede, onClose, onConfirm }) {
  const [reason, setReason] = useState('');
  const suggestions = ['No longer offered', 'Replaced by another', 'Priced wrong, being redone',
    'Resigned', 'Contract ended', 'Seasonal, back later'];

  return (
    <Sheet title={title} subtitle={lede} onClose={onClose}>
      <Section label="WHY">
        <Field id="reason" label="REASON" value={reason} autoFocus
          hint="Required. It goes in the audit log and shows on the retired list."
          onChange={e => setReason(e.target.value)} />
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {suggestions.map(s => (
            <button key={s} type="button" className="chip chip-neutral chip-s"
              style={{ border: 0, cursor: 'pointer' }} onClick={() => setReason(s)}>{s}</button>
          ))}
        </div>
      </Section>
      <div style={{ flex: 1 }} />
      <Divider />
      <div style={{ padding: 16 }}>
        <Button variant="danger" disabled={!reason.trim()} onClick={() => onConfirm(reason.trim())}>
          Retire it
        </Button>
      </div>
    </Sheet>
  );
}
